import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CircularProgress, Dialog, DialogContent, IconButton, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { DeleteOutline, FileDownloadOutlined, VisibilityOutlined } from '@mui/icons-material'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import axiosInstant from '../../api'
import { rmnAuditReportEndpoint, rmnAuditReportsEndpoint } from '../../api/endpoints'
import { buildReportDownload, downloadBlob } from './reportFiles'
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog'
import { DataTable, DataTableColumn } from '../DataTable/DataTable'
import { scoreCls } from '../PdpScoreStatus/PdpScoreStatus'
import { RmnAuditReportType } from '../../types/rmn'
import { PdpAudit } from './types'
import {
  LIST_LABEL_HEADERS,
  PAGE_SIZE,
  POLL_INTERVAL_MS,
  ReportDialogBody,
  RmnAuditReportRow,
  SECTIONS,
  rowLabel,
  strategyQueryForRow,
  unopenableCaption,
  useRmnProposalPath,
} from './auditReportShared'

// One report kind's history: paged list + 8s poll while generating +
// per-row View/Download/Delete. Extracted from the retired PDP Audit page
// (TDC-1499 phase 3) so the Catalog pages can each mount their own kind.
// The "New …" button renders only when `onNew` is provided — the Catalog
// pages create reports from their own per-row buttons instead.
export function ReportHistorySection({
  reportType,
  refreshKey = 0,
  title,
  emptyMessage,
  onNew,
  newLabel,
  onRowsChange,
}: {
  reportType: RmnAuditReportType
  refreshKey?: number
  title?: string
  emptyMessage?: string
  onNew?: () => void
  newLabel?: string
  // Fired whenever the loaded rows change (initial load, refreshKey reload,
  // generating poll) — the Catalog pages read the generating set from it to
  // disable their per-row Run report buttons while a report is in flight.
  onRowsChange?: (rows: RmnAuditReportRow[]) => void
}) {
  const [rows, setRows] = useState<RmnAuditReportRow[]>([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewing, setViewing] = useState<RmnAuditReportRow | null>(null)
  const navigate = useNavigate()
  const proposalPath = useRmnProposalPath()
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [pendingDelete, setPendingDelete] = useState<RmnAuditReportRow | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Latest-callback ref so the rows effect doesn't churn on parent renders.
  const onRowsChangeRef = useRef(onRowsChange)
  onRowsChangeRef.current = onRowsChange
  useEffect(() => {
    onRowsChangeRef.current?.(rows)
  }, [rows])

  // Network-scoped DELETE — available to every rmn-authorized user
  // (internal with access and brand roles alike).
  const handleDelete = async () => {
    if (!pendingDelete) return
    const label = rowLabel(pendingDelete)
    setDeleting(true)
    try {
      await axiosInstant.delete(rmnAuditReportEndpoint(pendingDelete.id))
      setRows((prev) => prev.filter((r) => r.id !== pendingDelete.id))
      setCount((c) => Math.max(0, c - 1))
      setPendingDelete(null)
      toast.success(`Deleted the ${label} report`)
    } catch {
      toast.error(`Couldn't delete the ${label} report. Please try again.`)
    } finally {
      setDeleting(false)
    }
  }

  // Client-side build: single PDF for pdp rows, synthesis-PDF + audits/
  // folder ZIP for the rest (see reportFiles.tsx).
  const handleDownload = async (row: RmnAuditReportRow) => {
    setDownloadingId(row.id)
    const toastId = toast.loading(
      row.report_type === 'pdp'
        ? 'Preparing PDF…'
        : 'Building the report package… this can take a minute.',
    )
    try {
      const { blob, filename } = await buildReportDownload(row)
      downloadBlob(blob, filename)
      toast.success('Report downloaded', { id: toastId })
    } catch {
      toast.error("Couldn't build the report file. Please try again.", { id: toastId })
    } finally {
      setDownloadingId(null)
    }
  }

  const load = useCallback(
    async (pageNum: number, append: boolean) => {
      setLoading(true)
      try {
        const res = await axiosInstant.get(rmnAuditReportsEndpoint, {
          params: { report_type: reportType, page: pageNum, limit: PAGE_SIZE },
        })
        const nextRows = (res.data?.rows ?? []) as RmnAuditReportRow[]
        setRows((prev) => (append ? [...prev, ...nextRows] : nextRows))
        setCount(res.data?.count ?? 0)
        setError(null)
      } catch {
        setError('Failed to load past reports')
      } finally {
        setLoading(false)
      }
    },
    [reportType],
  )

  useEffect(() => {
    setPage(1)
    setRows([])
    load(1, false)
  }, [load, refreshKey])

  // While any visible row is still generating, silently refresh the loaded
  // window every ~8s so rows flip to ready/error without user action.
  const hasGenerating = rows.some((r) => r.status === 'generating')
  useEffect(() => {
    if (!hasGenerating) return undefined
    const id = setInterval(async () => {
      try {
        const res = await axiosInstant.get(rmnAuditReportsEndpoint, {
          params: { report_type: reportType, page: 1, limit: page * PAGE_SIZE },
        })
        setRows((res.data?.rows ?? []) as RmnAuditReportRow[])
        setCount(res.data?.count ?? 0)
      } catch {
        // Keep the stale rows; the next tick retries.
      }
    }, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [hasGenerating, reportType, page])

  const loadMore = () => {
    const next = page + 1
    setPage(next)
    load(next, true)
  }

  const section = SECTIONS[reportType]
  const iconBtn =
    'w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed'
  const pill = 'text-[11px] font-bold uppercase tracking-[0.5px] rounded-full px-2.5 py-1 whitespace-nowrap'

  const columns: DataTableColumn<RmnAuditReportRow>[] = [
    {
      key: 'subject',
      header: LIST_LABEL_HEADERS[reportType],
      render: (row) => {
        const label = rowLabel(row)
        // Single-PDP rows are labelled by their URL; the multi-page kinds
        // show the category/brand name with its URL underneath. Brand
        // reports may have no representative URL (the crawler doesn't
        // always find a brand page).
        const subUrl = row.url && row.url !== label ? row.url : null
        return (
          <div className="min-w-0">
            {subUrl || !row.url ? (
              <div className="font-semibold text-text-1 truncate max-w-[380px]" title={label}>
                {label}
              </div>
            ) : (
              <a
                href={row.url}
                target="_blank"
                rel="noopener noreferrer"
                title={row.url}
                className="block font-semibold text-text-1 hover:text-accent-text truncate max-w-[380px]"
              >
                {label}
              </a>
            )}
            {subUrl && (
              <a
                href={subUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={subUrl}
                className="block text-[11px] text-muted hover:text-accent-text truncate max-w-[380px]"
              >
                {subUrl}
              </a>
            )}
          </div>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        if (row.status === 'generating') {
          return <span className={`${pill} text-accent-text bg-tint`}>Generating…</span>
        }
        if (row.status === 'error') {
          return (
            <Tooltip title={row.generation_error || 'Report generation failed'}>
              <span className={`${pill} text-[#c62828] bg-[#FBEFEF]`}>Failed</span>
            </Tooltip>
          )
        }
        return <span className={`${pill} text-[#2E7D32] bg-[#E8F5E9]`}>Ready</span>
      },
    },
    ...(reportType === 'pdp'
      ? [
          {
            key: 'score',
            header: 'PDP Score',
            render: (row: RmnAuditReportRow) => {
              const overall = (row.report as PdpAudit | null)?.scores?.overall
              if (row.status !== 'ready' || typeof overall !== 'number') {
                return <span className="text-muted">—</span>
              }
              return <span className={`text-[14px] font-bold ${scoreCls(overall)}`}>{overall}</span>
            },
          },
        ]
      : []),
    {
      key: 'created',
      header: 'Created',
      render: (row) => (
        <span className="text-[12px] text-text-2 whitespace-nowrap">
          {row.created_at ? dayjs(row.created_at).format('MMM DD, YYYY h:mm A') : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => {
        const openable = row.status !== 'generating' && !!row.report
        const unavailable = openable ? undefined : unopenableCaption(row)
        return (
          <div className="flex items-center justify-end gap-1">
            <Tooltip title={unavailable || 'View'}>
              <span>
                <button
                  type="button"
                  aria-label="View report"
                  disabled={!openable}
                  onClick={() => setViewing(row)}
                  className={iconBtn}
                >
                  <VisibilityOutlined sx={{ fontSize: 15 }} />
                </button>
              </span>
            </Tooltip>
            <Tooltip title={unavailable || 'Download report'}>
              <span>
                <button
                  type="button"
                  aria-label="Download report"
                  disabled={!openable || downloadingId === row.id}
                  onClick={() => handleDownload(row)}
                  className={iconBtn}
                >
                  {downloadingId === row.id ? (
                    <CircularProgress size={13} />
                  ) : (
                    <FileDownloadOutlined sx={{ fontSize: 15 }} />
                  )}
                </button>
              </span>
            </Tooltip>
            {/* Deletable once settled — failed rows especially. Generating
                rows wait: the background job still writes to them. */}
            <Tooltip title={row.status === 'generating' ? 'Still generating' : 'Delete report'}>
              <span>
                <button
                  type="button"
                  aria-label="Delete report"
                  disabled={row.status === 'generating' || downloadingId === row.id || deleting}
                  onClick={() => setPendingDelete(row)}
                  className={iconBtn}
                >
                  <DeleteOutline sx={{ fontSize: 15 }} />
                </button>
              </span>
            </Tooltip>
            <button
              type="button"
              title={unavailable || 'Start a strategy from this report'}
              disabled={!openable}
              onClick={() => navigate(`${proposalPath}?${strategyQueryForRow(row)}`)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-border-token rounded-md bg-surface text-[11.5px] font-medium text-accent-text cursor-pointer hover:border-accent hover:bg-tint disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            >
              Create strategy
            </button>
          </div>
        )
      },
    },
  ]

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="text-[16px] font-bold text-text-1">{title || section.title}</h2>
        <div className="flex items-center gap-3">
          {count > 0 && <span className="text-[12px] text-muted">{count} total</span>}
          {onNew && (
            <button
              type="button"
              onClick={onNew}
              className="px-3 py-1.5 rounded-lg bg-accent text-white text-[12px] font-semibold whitespace-nowrap hover:opacity-90"
            >
              {newLabel || section.newLabel}
            </button>
          )}
        </div>
      </div>
      <DataTable<RmnAuditReportRow>
        columns={columns}
        rows={rows}
        loading={loading}
        totalCount={count}
        hasMore={rows.length < count}
        error={error}
        onLoadMore={loadMore}
        emptyMessage={emptyMessage || section.empty}
        getRowKey={(r) => r.id}
      />

      <Dialog open={!!viewing} onClose={() => setViewing(null)} fullWidth maxWidth="md">
        <IconButton
          onClick={() => setViewing(null)}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
          aria-label="Close report"
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>{viewing && <ReportDialogBody row={viewing} />}</DialogContent>
      </Dialog>

      {pendingDelete && (
        <ConfirmationDialog
          open
          title={`Delete ${SECTIONS[reportType].title.replace(/s$/, '')}`}
          description={`Delete the report for "${rowLabel(pendingDelete)}"? This cannot be undone.`}
          confirmLabel="Delete"
          onClose={() => setPendingDelete(null)}
          onConfirm={handleDelete}
          disabled={deleting}
        />
      )}
    </div>
  )
}
